import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Send, Plus, Check, Sparkles } from 'lucide-react';
import { useT } from '../../i18n';
import { Card } from './os2ui';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import AiTeamOsPage from './AiTeamOsPage';

/* AI Workspace — the team of AI agents lives here: chat, assign tasks, launch analysis. */

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = {
  rachel: { en: 'Chief of Staff', es: 'Jefa de Gabinete' },
  eva: { en: 'Operations Director', es: 'Directora de Operaciones' },
  stan: { en: 'Sales Director', es: 'Director de Ventas' },
  penny: { en: 'Marketing Director', es: 'Directora de Marketing' },
  sonny: { en: 'Distribution Director', es: 'Director de Distribución' },
  linda: { en: 'Finance Director', es: 'Directora Financiera' },
};

const SUGGESTIONS = {
  rachel: { es: ['¿Qué debería hacer hoy?', 'Resume la semana', '¿Cómo va la empresa?'], en: ['What should I do today?', 'Summarize the week', 'How is the company doing?'] },
  eva: { es: ['¿Todo operativo?', '¿Últimos eventos importantes?'], en: ['All systems ok?', 'Latest important events?'] },
  stan: { es: ['¿Qué leads llamo hoy?', 'Analiza mi pipeline'], en: ['Which leads should I call today?', 'Analyze my pipeline'] },
  penny: { es: ['Ideas de contenido para esta semana', '¿Qué artículo publico?'], en: ['Content ideas for this week', 'Which article should I publish?'] },
  sonny: { es: ['Plan de redes para hoy'], en: ['Social plan for today'] },
  linda: { es: ['¿Cómo van mis scores de crecimiento?', '¿Qué oportunidad SEO ataco primero?'], en: ['How are my growth scores?', 'Which SEO opportunity first?'] },
};

const renderMd = (text) => {
  const parts = String(text || '').split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => (i % 2 === 1 ? <b key={i}>{p}</b> : p));
};

const parseActions = (text) => {
  const actions = [];
  let clean = String(text || '').replace(/\[\[TASK:([a-z]+):([^\]]+)\]\]/gi, (_, ag, title) => {
    actions.push({ agent: ag.toLowerCase(), title: title.trim() });
    return '';
  });
  clean = clean.replace(/\[\[TASK[^\]]*$/i, '');
  return { clean: clean.trim(), actions };
};

function ActionCard({ act, isEs }) {
  const name = act.agent.charAt(0).toUpperCase() + act.agent.slice(1);
  const av = TEAM_AVATARS.find((x) => x.key === act.agent);
  return (
    <div className="mt-2 flex items-start gap-2.5 rounded-2xl border border-emerald-200 bg-emerald-50/70 px-3.5 py-2.5" data-testid="ws-action-card">
      {av && <img src={av.img} alt={name} className="w-8 h-8 rounded-full object-cover mt-0.5" />}
      <div className="min-w-0">
        <p className="text-[12px] font-bold text-emerald-800">✓ {isEs ? `Tarea asignada a ${name}` : `Task assigned to ${name}`}</p>
        <p className="text-[12.5px] text-gray-700 leading-snug">{act.title}</p>
        <p className="mt-1 text-[10.5px] text-emerald-700/80 font-semibold">
          {isEs ? 'Eva notificada · confirmación enviada a info@kitxens.com' : 'Eva notified · confirmation emailed to info@kitxens.com'}
        </p>
      </div>
    </div>
  );
}


const sid = (agent) => {
  const k = `kx_ws_session_${agent}`;
  try {
    let v = sessionStorage.getItem(k);
    if (!v) { v = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; sessionStorage.setItem(k, v); }
    return v;
  } catch { return 'anon'; }
};

const isDemoMode = () => { try { return sessionStorage.getItem('kx_demo') === '1'; } catch { return false; } };

const DEMO_REPLIES = {
  es: 'Con gusto. Según los datos del OS, hoy tienes 2 leads calientes (Taco Nación y Sakura Ramen House) y una oportunidad SEO de ~310 visitas/mes. Yo empezaría llamando a Taco Nación antes de las 12 — su engagement está en el pico. ¿Quieres que le asigne la tarea a Stan?',
  en: 'Gladly. Based on OS data, you have 2 hot leads today (Taco Nación and Sakura Ramen House) and an SEO opportunity worth ~310 visits/month. I would call Taco Nación before noon — their engagement is peaking. Want me to assign the task to Stan?',
};

function AgentCard({ a, active, isEs, taskCount, onClick }) {
  return (
    <button onClick={onClick} data-testid={`ws-agent-${a.key}`}
      className={`shrink-0 w-auto lg:w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-all lg:mb-2 ${active ? 'border-[#F77F11]/50 bg-orange-50 shadow-sm' : 'border-gray-100 bg-white hover:border-gray-200'}`}>
      <span className="relative shrink-0">
        <img src={a.img} alt={a.name} className="w-11 h-11 rounded-full object-cover" />
        <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-bold text-gray-900">{a.name}</span>
        <span className="block text-[11px] text-gray-400">{ROLES[a.key] ? (isEs ? ROLES[a.key].es : ROLES[a.key].en) : a.role}</span>
      </span>
      {taskCount > 0 && <span className="shrink-0 rounded-full bg-gray-900 text-white px-2 py-0.5 text-[10px] font-bold">{taskCount}</span>}
    </button>
  );
}

export default function AiWorkspacePage({ embedded = false }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const agents = useMemo(() => TEAM_AVATARS.filter((a) => ROLES[a.key]), []);
  const initial = (() => { try { return new URLSearchParams(window.location.search).get('agent') || 'rachel'; } catch { return 'rachel'; } })();
  const initialTab = (() => { try { return new URLSearchParams(window.location.search).get('tab') === 'team' && !embedded ? 'team' : 'chat'; } catch { return 'chat'; } })();
  const [tab, setTab] = useState(initialTab);
  const [agent, setAgent] = useState(ROLES[initial] ? initial : 'rachel');
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [taskInput, setTaskInput] = useState('');
  const [lastFailed, setLastFailed] = useState('');
  const endRef = useRef(null);
  const boxRef = useRef(null);
  const a = agents.find((x) => x.key === agent) || agents[0];

  const loadTasks = () => {
    if (isDemoMode()) {
      setTasks([{ id: 'd1', agent, title: isEs ? 'Preparar propuesta para Taco Nación' : 'Prepare proposal for Taco Nación', status: 'queued' }]);
      return;
    }
    fetch(`${API}/api/admin/ai-workspace/tasks?agent=${agent}`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => setTasks(j.tasks || [])).catch(() => setTasks([]));
  };

  useEffect(() => {
    setMsgs([]);
    loadTasks();
    if (!isDemoMode()) {
      fetch(`${API}/api/admin/ai-workspace/history?agent=${agent}&session_id=${sid(agent)}`, { credentials: 'include' })
        .then((r) => r.json()).then((j) => setMsgs(j.messages || [])).catch(() => {});
    }
  }, [agent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll ONLY the chat pane — never the page (scrollIntoView would drag the whole viewport down).
  useEffect(() => {
    const box = boxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [msgs]);

  const send = async (text) => {
    const t = (text || input).trim();
    if (!t || busy) return;
    setInput('');
    setBusy(true);
    setMsgs((m) => [...m, { role: 'user', text: t }, { role: 'assistant', text: '' }]);
    if (isDemoMode()) {
      const wantsAssign = msgs.length > 0 && /\b(s[ií]|yes|dale|ok|okay|claro|hazlo|as[ií]gna\w*|assign)\b/i.test(t);
      const reply = wantsAssign
        ? (isEs
          ? 'Hecho. Le asigné la tarea a Stan con prioridad alta y avisé a Eva para el seguimiento. También envié la confirmación por email a info@kitxens.com.\n[[TASK:stan:Llamar a Taco Nación antes de las 12]]'
          : 'Done. I assigned the task to Stan with high priority and notified Eva for follow-up. I also emailed the confirmation to info@kitxens.com.\n[[TASK:stan:Call Taco Nación before noon]]')
        : (isEs ? DEMO_REPLIES.es : DEMO_REPLIES.en);
      let i = 0;
      const iv = setInterval(() => {
        i += 6;
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: 'assistant', text: reply.slice(0, i) }; return c; });
        if (i >= reply.length) {
          clearInterval(iv);
          setBusy(false);
          if (wantsAssign) setTasks((x) => [{ id: `d${Date.now()}`, agent: 'stan', title: isEs ? 'Llamar a Taco Nación antes de las 12' : 'Call Taco Nación before noon', status: 'queued' }, ...x]);
        }
      }, 30);
      return;
    }
    try {
      const r = await fetch(`${API}/api/admin/ai-workspace/chat`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent, session_id: sid(agent), message: t }),
      });
      if (!r.ok || !r.body) throw new Error('chat failed');
      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let acc = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        const snapshot = acc;
        setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: 'assistant', text: snapshot }; return c; });
      }
      if (parseActions(acc).actions.length) loadTasks();
      setLastFailed('');
    } catch {
      setLastFailed(t);
      setMsgs((m) => {
        const c = [...m];
        c[c.length - 1] = {
          role: 'assistant', error: true, failedText: t,
          text: isEs
            ? `${a.name} no está disponible en este momento. Tu mensaje quedó guardado — inténtalo de nuevo.`
            : `${a.name} is temporarily unavailable. Your message was saved — try again.`,
        };
        return c;
      });
    }
    setBusy(false);
  };

  const retry = () => {
    if (!lastFailed || busy) return;
    setMsgs((m) => (m.length >= 2 ? m.slice(0, -2) : m));
    send(lastFailed);
  };

  const saveFailedAsTask = async (text) => {
    if (!text) return;
    setLastFailed('');
    setMsgs((m) => (m.length >= 1 && m[m.length - 1].error ? m.slice(0, -1) : m));
    if (isDemoMode()) { setTasks((x) => [{ id: `d${Date.now()}`, agent, title: text, status: 'queued' }, ...x]); return; }
    await fetch(`${API}/api/admin/ai-workspace/tasks`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent, title: text.slice(0, 160) }),
    });
    loadTasks();
  };

  const addTask = async () => {
    const t = taskInput.trim();
    if (!t) return;
    setTaskInput('');
    if (isDemoMode()) { setTasks((x) => [{ id: `d${Date.now()}`, agent, title: t, status: 'queued' }, ...x]); return; }
    await fetch(`${API}/api/admin/ai-workspace/tasks`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent, title: t }),
    });
    loadTasks();
  };

  const doneTask = async (id) => {
    if (isDemoMode()) { setTasks((x) => x.map((tk) => (tk.id === id ? { ...tk, status: 'done' } : tk))); return; }
    await fetch(`${API}/api/admin/ai-workspace/tasks/${id}/done`, { method: 'POST', credentials: 'include' });
    loadTasks();
  };

  return (
    <div data-testid="ai-workspace" className="h-full">
      {!embedded && (
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">AI Executive Team</h1>
          <p className="mt-1 text-[12.5px] text-gray-500">
            {isEs ? 'Tu equipo ejecutivo AI vive aquí: habla con cualquiera, asigna tareas y mira su actividad en vivo.' : 'Your AI executive team lives here: talk to anyone, assign tasks and watch their live activity.'}
          </p>
        </div>
        <div className="inline-flex rounded-full border border-gray-200 bg-white p-1" data-testid="ws-tabs">
          <button onClick={() => setTab('chat')} data-testid="ws-tab-chat"
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${tab === 'chat' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            💬 Chat
          </button>
          <button onClick={() => setTab('team')} data-testid="ws-tab-team"
            className={`rounded-full px-4 py-1.5 text-[12px] font-bold transition-colors ${tab === 'team' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            {isEs ? '⚡ Actividad del equipo' : '⚡ Team activity'}
          </button>
        </div>
      </div>
      )}
      {tab === 'team' && !embedded ? <AiTeamOsPage embedded /> : (
      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_260px] gap-4 items-start">
        {/* Agent roster — horizontal strip on mobile, vertical list on desktop */}
        <div data-testid="ws-roster">
          <p className="mb-2 text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400">{isEs ? 'Tu equipo' : 'Your team'}</p>
          <div className="flex gap-2 overflow-x-auto pb-2 lg:block lg:overflow-visible lg:pb-0">
            {agents.map((ag) => (
              <AgentCard key={ag.key} a={ag} active={ag.key === agent} isEs={isEs}
                taskCount={ag.key === agent ? tasks.filter((t) => t.status === 'queued').length : 0}
                onClick={() => setAgent(ag.key)} />
            ))}
          </div>
        </div>

        {/* Chat */}
        <Card className="flex flex-col" data-testid="ws-chat">
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100">
            <img src={a.img} alt={a.name} className="w-8 h-8 rounded-full object-cover" />
            <div>
              <p className="text-[13.5px] font-bold text-gray-900 leading-tight">{a.name}</p>
              <p className="text-[10.5px] text-emerald-600 font-semibold">● {isEs ? 'en línea' : 'online'} · {ROLES[agent] ? (isEs ? ROLES[agent].es : ROLES[agent].en) : ''}</p>
            </div>
            <Sparkles className="ml-auto w-4 h-4 text-[#F77F11]" />
          </div>
          <div ref={boxRef} className="flex-1 min-h-[300px] md:min-h-[380px] max-h-[52vh] overflow-y-auto p-4 space-y-3" data-testid="ws-messages">
            {msgs.length === 0 && (
              <div className="pt-6 text-center">
                <p className="text-[13px] text-gray-400">{isEs ? `Habla con ${a.name} — te responde con datos reales del OS.` : `Talk to ${a.name} — answers use real OS data.`}</p>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {(SUGGESTIONS[agent]?.[isEs ? 'es' : 'en'] || []).map((sug) => (
                    <button key={sug} onClick={() => send(sug)} data-testid="ws-suggestion"
                      className="rounded-full border border-gray-200 px-3 py-1.5 text-[12px] text-gray-600 hover:border-[#F77F11] hover:text-[#d8651a] transition-colors">
                      {sug}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {msgs.map((m, i) => {
              if (m.error) {
                return (
                  <div key={i} className="flex justify-start">
                    <div className="max-w-[86%] rounded-2xl border border-amber-200 bg-amber-50/80 px-3.5 py-3" data-testid="ws-chat-error">
                      <p className="text-[12.5px] text-amber-900 leading-relaxed">{m.text}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button onClick={retry} data-testid="ws-chat-retry"
                          className="rounded-full bg-gray-900 text-white px-3.5 py-1.5 text-[11.5px] font-bold hover:bg-[#F77F11] transition-colors">
                          {isEs ? 'Reintentar' : 'Retry'}
                        </button>
                        <button onClick={() => saveFailedAsTask(m.failedText)} data-testid="ws-chat-save-task"
                          className="rounded-full border border-amber-300 text-amber-800 px-3.5 py-1.5 text-[11.5px] font-bold hover:bg-amber-100 transition-colors">
                          {isEs ? 'Guardar como tarea' : 'Save as task'}
                        </button>
                        <a href="mailto:info@kitxens.com" className="rounded-full px-3.5 py-1.5 text-[11.5px] font-semibold text-amber-700/80 hover:text-amber-900" data-testid="ws-chat-support">
                          {isEs ? 'Soporte' : 'Support'}
                        </a>
                      </div>
                    </div>
                  </div>
                );
              }
              const parsed = m.role === 'assistant' ? parseActions(m.text) : null;
              return (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className="max-w-[82%]">
                    <div className={`rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-gray-900 text-white rounded-br-sm' : 'bg-gray-50 border border-gray-100 text-gray-800 rounded-bl-sm'}`}>
                      {m.role === 'user'
                        ? renderMd(m.text)
                        : (parsed.clean || parsed.actions.length
                          ? renderMd(parsed.clean)
                          : <span className="inline-flex gap-1"><span className="animate-bounce">·</span><span className="animate-bounce [animation-delay:120ms]">·</span><span className="animate-bounce [animation-delay:240ms]">·</span></span>)}
                    </div>
                    {parsed?.actions.map((a2, j) => <ActionCard key={j} act={a2} isEs={isEs} />)}
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <div className="flex items-center gap-2 border-t border-gray-100 p-3">
            <input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={isEs ? `Escribe a ${a.name}…` : `Message ${a.name}…`}
              className="flex-1 rounded-full border border-gray-200 px-4 py-2.5 text-[13px] outline-none focus:border-[#F77F11]"
              data-testid="ws-chat-input" />
            <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="ws-chat-send"
              className="w-10 h-10 rounded-full bg-[#F77F11] text-white flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </Card>

        {/* Tasks */}
        <div data-testid="ws-tasks">
          <p className="mb-2 text-[10px] uppercase tracking-[0.15em] font-bold text-gray-400">{isEs ? `Tareas de ${a.name}` : `${a.name}'s tasks`}</p>
          <Card className="p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <input value={taskInput} onChange={(e) => setTaskInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addTask()}
                placeholder={isEs ? 'Asignar tarea…' : 'Assign a task…'}
                className="flex-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12px] outline-none focus:border-[#F77F11]"
                data-testid="ws-task-input" />
              <button onClick={addTask} className="w-7 h-7 rounded-lg bg-gray-900 text-white flex items-center justify-center hover:bg-[#F77F11] transition-colors" data-testid="ws-task-add">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            {tasks.length === 0 && <p className="py-2 text-center text-[11.5px] text-gray-300">{isEs ? 'Sin tareas asignadas.' : 'No tasks assigned.'}</p>}
            {tasks.map((t) => (
              <div key={t.id} className="flex items-start gap-2 py-1.5 border-t border-gray-50 first:border-0" data-testid={`ws-task-${t.id}`}>
                <button onClick={() => t.status !== 'done' && doneTask(t.id)}
                  className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${t.status === 'done' ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-emerald-500'}`}>
                  {t.status === 'done' && <Check className="w-2.5 h-2.5 text-white" />}
                </button>
                <span className={`text-[12px] leading-snug ${t.status === 'done' ? 'text-gray-300 line-through' : 'text-gray-700'}`}>{t.title}</span>
              </div>
            ))}
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
